import { TimePicker } from "@tql/ui-components-react/kendo/dateinputs";
import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { memo, useCallback, useEffect, useState } from "react";

import type { EditDescriptor } from "~/features/commodities/hooks/use-commodity-editing";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema";
import {
	formatTimeToString,
	parseTimeString,
} from "~/shared/utils/time-utils.ts";

type TimeRangeCellProps = GridCustomCellProps & {
	edit: EditDescriptor;
};

// Extended type for searchable data items
type SearchableAddressBookItem = AddressBookItemSummary & {
	originalHoursOpen?: string;
	originalHoursClose?: string;
	// These are added by the modal's data transformation for filtering
	hoursOpen?: string;
	hoursClose?: string;
};

type TimePickerChangeEvent = {
	value: Date | null;
};

export const TimeRangeCell = memo(
	({ edit, ...gridProps }: TimeRangeCellProps) => {
		const dataItem = gridProps.dataItem as
			| SearchableAddressBookItem
			| undefined;

		const [localHoursOpen, setLocalHoursOpen] = useState<Date | null>(null);
		const [localHoursClose, setLocalHoursClose] = useState<Date | null>(null);

		const isInEditMode = dataItem
			? edit[dataItem.addressBookItemId] === true
			: false;

		// Use original values if available (for searchable data), otherwise use current values
		const actualHoursOpen =
			dataItem?.originalHoursOpen ?? dataItem?.hoursOpen ?? "";
		const actualHoursClose =
			dataItem?.originalHoursClose ?? dataItem?.hoursClose ?? "";

		useEffect(() => {
			if (dataItem) {
				setLocalHoursOpen(parseTimeString(actualHoursOpen));
				setLocalHoursClose(parseTimeString(actualHoursClose));
			}
		}, [dataItem, isInEditMode, actualHoursOpen, actualHoursClose]);

		const commitChanges = useCallback(() => {
			if (gridProps.onChange && dataItem) {
				const changes = [];
				const newHoursOpen = formatTimeToString(localHoursOpen);
				const newHoursClose = formatTimeToString(localHoursClose);

				if (newHoursOpen !== actualHoursOpen) {
					changes.push({ field: "hoursOpen", value: newHoursOpen });
				}
				if (newHoursClose !== actualHoursClose) {
					changes.push({ field: "hoursClose", value: newHoursClose });
				}

				for (const { field, value } of changes) {
					gridProps.onChange?.({
						dataIndex: 0,
						dataItem: gridProps.dataItem as AddressBookItemSummary,
						field,
						syntheticEvent: {} as React.SyntheticEvent,
						value,
					});
				}
			}
		}, [
			localHoursOpen,
			localHoursClose,
			dataItem,
			gridProps,
			actualHoursOpen,
			actualHoursClose,
		]);

		const handleHoursOpenChange = useCallback(
			(event: TimePickerChangeEvent) => {
				setLocalHoursOpen(event.value);
			},
			[],
		);

		const handleHoursCloseChange = useCallback(
			(event: TimePickerChangeEvent) => {
				setLocalHoursClose(event.value);
			},
			[],
		);

		const handleBlur = useCallback(() => {
			commitChanges();
		}, [commitChanges]);

		// Guard against undefined dataItem
		if (!dataItem) {
			return <td {...gridProps.tdProps}></td>;
		}

		const displayValue = `${actualHoursOpen || ""} - ${actualHoursClose || ""}`;

		return (
			<td {...gridProps.tdProps}>
				{isInEditMode ? (
					<div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
						<TimePicker
							onBlur={handleBlur}
							onChange={handleHoursOpenChange}
							placeholder={"Open"}
							value={localHoursOpen}
						/>
						<span>-</span>
						<TimePicker
							onBlur={handleBlur}
							onChange={handleHoursCloseChange}
							placeholder={"Close"}
							value={localHoursClose}
						/>
					</div>
				) : (
					displayValue
				)}
			</td>
		);
	},
);

TimeRangeCell.displayName = "TimeRangeCell";
